import { useMutation } from '@tanstack/react-query';
import { useDependency, useOptionalDependency } from 'ditox-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { parseURL, stringifyParsedURL } from 'ufo';
import { DYNAMIC_ENDPOINT_MANAGER_TOKEN, TRANSCODER_TOKEN, UPPY_TOKEN } from '@/container';
import useIsAdminSession from '@/hooks/conversations/use-is-admin-session';
import useSystemSetting from '@/hooks/files/use-system-setting';
import type { UploadFileResponseType } from '@/hooks/files/use-upload-file';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { ResponseError } from '@/models/response';
import type { Uploadable } from '@/models/uploadable';
import { promiseLogger, SENTRY_LOG } from '@/services/sentry';
import { useConfigurationStore } from '@/stores/configuration';
import { addToast, addTranslateToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { getMediaMetadata } from '@/utils/media-metadata';
import { createPromiseWithResolvers } from '@/utils/promise';
import { isRetryEnabledError, RETRY_DELAYS } from '@/utils/query-client';
import { convertFileToUploadable, convertUploadableToFile, convertUploadableToUppyFile } from '@/utils/uploadable';

export default function useUploadMessageFile(type: ConversationType, id: Nullable<number>) {
  const { t } = useTranslation();
  const uppy = useDependency(UPPY_TOKEN);
  const dynamicEndpointManager = useDependency(DYNAMIC_ENDPOINT_MANAGER_TOKEN);
  const { data, getFileSizeLimit, isLoading: isSystemSettingLoading } = useSystemSetting();
  const transcoder = useOptionalDependency(TRANSCODER_TOKEN);
  const isAdminSession = useIsAdminSession(type, id);

  const mutation = useMutation<UploadFileResponseType, ResponseError, Uploadable>({
    mutationKey: ['/UploadFile'],
    mutationFn: async (params) => {
      const { resolve, reject, promise } = createPromiseWithResolvers<UploadFileResponseType>();

      const endpoint = parseURL(useConfigurationStore.getState().endpoint.FILEUPLOAD_ENDPOINT);
      endpoint.pathname = '/v1/api/UploadFile';
      uppy.getPlugin('tus-upload')?.setOptions({
        endpoint: stringifyParsedURL(endpoint),
        chunkSize: data?.uploadChunkSize ?? 1024 * 1024 * 6,
      });

      const file = await convertUploadableToUppyFile(params, { ChatSessionID: type === 'session' ? (id ?? 0) : 0 });
      uppy.addFile(file);

      uppy.on('upload-error', (_file, error, response) => {
        if (response?.body) {
          const code = response.body.Code;
          let message = response.body.Msg;
          switch (code) {
            case 700001:
              message = t('errors.invalid-file');
              break;
            case 700002:
              message = t('errors.file-size-limit', { limit: `${response.body.LocalizedParams}MB` });
              break;
            case 700003:
              message = t('upload.failed');
              break;
            case 300001:
              reject('NOT_AUTHENTICATED');
              break;
            default:
              reject('UNKNOWN_ERROR');
              break;
          }
          reject(new ResponseError(code, message));
        } else {
          reject(new ResponseError(-1, error.message));
        }
      });

      const response = await uppy.upload();

      const firstSuccessfulFile = response?.successful?.at(0);
      if (firstSuccessfulFile?.uploadURL) {
        resolve({
          Code: 0,
          Msg: '',
          FileUrl: firstSuccessfulFile.uploadURL,
        });
      }

      return promise;
    },
    retry: (failureCount, error) => isRetryEnabledError(error) && failureCount <= 5,
    retryDelay: (failureCount) => RETRY_DELAYS[failureCount] ?? 10000,
    onSettled: () => {
      uppy.clear();
    },
    onError: (error) => {
      if (ResponseError.isResponseError(error)) {
        addToast({
          type: 'error',
          message: error.message,
        });
      } else {
        const endpoint = useConfigurationStore.getState().endpoint.FILEUPLOAD_ENDPOINT;
        dynamicEndpointManager.revokeFileUploadEndpoint(endpoint);
        addTranslateToast({
          type: 'error',
          message: 'upload.failed',
        });
      }
    },
  });

  const upload = useCallback(
    async (input: Uploadable) => {
      let uploadable = input;

      mutation.reset();

      const fileSizeLimit = getFileSizeLimit(uploadable.type.split('/')[0], isAdminSession);
      if (uploadable.size >= fileSizeLimit) {
        addToast({
          type: 'error',
          message: t('errors.file-size-limit', { limit: readableFileSize(fileSizeLimit) }),
        });

        throw 'FILE_TOO_LARGE';
      }

      // NOTE: compression
      if (uploadable.compress && transcoder) {
        try {
          let output = await convertUploadableToFile(uploadable);
          const fileType = uploadable.type.split('/')[0];

          switch (fileType) {
            case 'video':
              output = await transcoder.transcodeVideo(output);
              break;
            case 'image':
              output = await transcoder.transcodeImage(output);
              break;
            case 'audio':
              output = await transcoder.transcodeAudio(output);
              break;
          }

          uploadable = convertFileToUploadable(output);
          uploadable.compress = false;
        } catch (e) {
          console.error(e);
        }
      }

      const response = await promiseLogger({
        name: SENTRY_LOG.UPLOAD,
        requestAttributes: {
          'file-size': `${uploadable.size}`,
          'file-type': uploadable.type,
        },
        action: () => mutation.mutateAsync(uploadable),
      });

      const fileUrl = new URL(response.FileUrl);

      // NOTE: determine dimensions
      if (fileUrl.searchParams.has('w') || fileUrl.searchParams.has('t')) return response;

      const dimensions = await getMediaMetadata(uploadable);
      for (const [key, value] of Object.entries(dimensions)) {
        if (value) fileUrl.searchParams.set(key.charAt(0), value);
      }

      return {
        ...response,
        FileUrl: fileUrl.href,
      };
    },
    [isAdminSession, mutation, t, transcoder],
  );

  return {
    upload,
    isLoading: isSystemSettingLoading || mutation.isPending,
    ...mutation,
  };
}
